import React from 'react';
import { Control, Controller, FieldErrors, get } from 'react-hook-form';
import { Question, Option } from '../../api/types/forms';
import { TextField, Checkbox, FormControlLabel, RadioGroup, Radio, Select, MenuItem, FormControl, InputLabel, FormHelperText, ListItemText, Typography } from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import FileUploader from './inputs/FileUploader';

interface DynamicFormQuestionProps {
    question: Question;
    fieldName: string;
    control: Control<any>;
    errors: FieldErrors<any>;
    labelOverride?: string;
    rulesOverride?: Record<string, any>;
    organizationId?: string;
    programId?: string;
    inspectionProgramId?: string;
    itemId?: string;
    isAnyFileUploading?: boolean;
    onUploadingChange?: (fieldName: string, isUploading: boolean) => void;
}

const DynamicFormQuestion: React.FC<DynamicFormQuestionProps> = ({
    question,
    fieldName,
    control,
    errors,
    labelOverride,
    rulesOverride,
    organizationId,
    programId,
    inspectionProgramId,
    itemId,
    isAnyFileUploading,
    onUploadingChange
}) => {
    const label = labelOverride || `${question.text}${question.mandatory ? '*' : ''}`;
    const rules = rulesOverride; // Rules should be passed in designated prop or handled by parent, but simpler to expect passed in

    const fieldError = get(errors, fieldName);
    const helperText = (fieldError?.message as string) || question.help_text || ' ';
    const helperTextProps = { sx: { minHeight: '1.25em' } };

    switch (question.type) {
        case 'text': case 'textarea': case 'number':
            return (
                <Controller name={fieldName} control={control} rules={rules} defaultValue={''}
                    render={({ field }) => (
                        <TextField {...field} id={fieldName}
                            type={question.type === 'number' ? 'number' : 'text'}
                            label={label} placeholder={question.placeholder || ''} fullWidth
                            multiline={question.type === 'textarea'} rows={question.type === 'textarea' ? 3 : 1}
                            error={!!fieldError}
                            helperText={helperText}
                            FormHelperTextProps={helperTextProps}
                            variant="outlined" />
                    )} />
            );
        case 'date':
            return (
                <Controller name={fieldName} control={control} rules={rules} defaultValue={null}
                    render={({ field }) => {
                        const dateValue = field.value ? (typeof field.value === 'string' ? new Date(field.value) : field.value) : null;
                        return (
                            <DatePicker
                                label={label}
                                value={dateValue}
                                onChange={(newValue) => {
                                    field.onChange(newValue ? newValue.toISOString() : '');
                                }}
                                slotProps={{
                                    textField: {
                                        id: fieldName,
                                        fullWidth: true,
                                        error: !!fieldError,
                                        helperText,
                                        FormHelperTextProps: helperTextProps,
                                        variant: 'outlined',
                                        placeholder: '',
                                    },
                                }}
                            />
                        );
                    }} />
            );
        case 'datetime':
            return (
                <Controller name={fieldName} control={control} rules={rules} defaultValue={null}
                    render={({ field }) => {
                        const dateValue = field.value ? (typeof field.value === 'string' ? new Date(field.value) : field.value) : null;
                        return (
                            <DateTimePicker
                                label={label}
                                value={dateValue}
                                onChange={(newValue) => {
                                    field.onChange(newValue ? newValue.toISOString() : '');
                                }}
                                slotProps={{
                                    textField: {
                                        id: fieldName,
                                        fullWidth: true,
                                        error: !!fieldError,
                                        helperText,
                                        FormHelperTextProps: helperTextProps,
                                        variant: 'outlined',
                                        placeholder: '',
                                    },
                                }}
                            />
                        );
                    }} />
            );
        case 'checkbox':
            return (
                <FormControl error={!!fieldError} sx={{ display: 'block' }}>
                    <Controller name={fieldName} control={control} rules={rules} defaultValue={false}
                        render={({ field }) => <FormControlLabel control={<Checkbox {...field} checked={field.value || false} />} label={label} />} />
                    <FormHelperText sx={{ minHeight: '1.25em' }}>{helperText}</FormHelperText>
                </FormControl>
            );
        case 'radio':
            return (
                <FormControl component="fieldset" error={!!fieldError}>
                    <InputLabel shrink component="legend">{label}</InputLabel>
                    <Controller name={fieldName} control={control} rules={rules} defaultValue={''}
                        render={({ field }) => (
                            <RadioGroup {...field} row>
                                {(question.options || []).map((opt: Option) => <FormControlLabel key={opt.value} value={opt.value} control={<Radio />} label={opt.label} />)}
                            </RadioGroup>
                        )} />
                    <FormHelperText sx={{ minHeight: '1.25em' }}>{helperText}</FormHelperText>
                </FormControl>
            );
        case 'select':
            return (
                <FormControl fullWidth error={!!fieldError}>
                    <InputLabel id={`${fieldName}-label`}>{label}</InputLabel>
                    <Controller name={fieldName} control={control} rules={rules} defaultValue={''}
                        render={({ field }) => (
                            <Select {...field} labelId={`${fieldName}-label`} label={label}>
                                <MenuItem value=""><em>{question.placeholder || 'Seleccione...'}</em></MenuItem>
                                {(question.options || []).map((opt: Option) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                            </Select>
                        )} />
                    <FormHelperText sx={{ minHeight: '1.25em' }}>{helperText}</FormHelperText>
                </FormControl>
            );
        case 'multiselect':
            return (
                <FormControl fullWidth error={!!fieldError}>
                    <InputLabel id={`${fieldName}-label`}>{label}</InputLabel>
                    <Controller name={fieldName} control={control} rules={rules} defaultValue={[]}
                        render={({ field }) => (
                            <Select {...field} labelId={`${fieldName}-label`} label={label} multiple value={field.value || []}
                                renderValue={(selected) => ((selected as string[]).map(val => {
                                    // Try exact match first
                                    let option = question.options?.find(opt => opt.value === val);
                                    // If not found, try with type conversion (handles string/number mismatches)
                                    if (!option) {
                                        option = question.options?.find(opt => String(opt.value) === String(val));
                                    }
                                    return option?.label || val;
                                }).join(', '))}>
                                {(question.options || []).map((opt: Option) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                            <Checkbox checked={(field.value || []).some((val: any) => val === opt.value || String(val) === String(opt.value))} />
                                        <ListItemText primary={opt.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        )} />
                    <FormHelperText sx={{ minHeight: '1.25em' }}>{helperText}</FormHelperText>
                </FormControl>
            );
        case 'file':
            return (
                <Controller
                    name={fieldName}
                    control={control}
                    rules={rules}
                    defaultValue={[]}
                    render={({ field }) => (
                        <FileUploader
                            value={Array.isArray(field.value) ? field.value : []}
                            onChange={field.onChange}
                            organizationId={organizationId || ''}
                            programId={programId || ''}
                            inspectionProgramId={inspectionProgramId || ''}
                            itemId={itemId || ''}
                            disabled={!!fieldError || isAnyFileUploading}
                            fieldName={fieldName}
                            onUploadingChange={onUploadingChange}
                        />
                    )}
                />
            );
        default:
            return <Typography key={question.id} color="error">Unsupported question type: {question.type}</Typography>;
    }
};

export default DynamicFormQuestion;
